import { useEffect, useMemo, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import identitySubscriptionService from "services/subscriptions";
import {
  subscriptionFeaturesFor,
  subscriptionTier,
} from "workspaces/subscriptionFeatures";

const SUBSCRIPTION_FEATURE_CACHE_TTL_MS = 5 * 60 * 1000;
const subscriptionFeatureCache = new Map();
const pendingSubscriptionFeatureLoads = new Map();

const workspaceIdFor = (workspace = {}) => workspace?.id || workspace?.workspaceId || "";
const subscriptionFeatureCacheKey = ({ workspaceId, actorId }) =>
  `${actorId || "anonymous"}:${workspaceId || "none"}`;
const freshCacheEntry = (entry, now = Date.now()) =>
  entry && now - entry.fetchedAt < SUBSCRIPTION_FEATURE_CACHE_TTL_MS;

export default function useWorkspaceSubscriptionFeatures() {
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const [subscription, setSubscription] = useState(null);
  const actorId = user?.id || user?.userId || user?.uid || "";
  const workspaceId = selectedWorkspaceId || workspaceIdFor(selectedWorkspace);
  const cacheKey = subscriptionFeatureCacheKey({ workspaceId, actorId });
  const cached = subscriptionFeatureCache.get(cacheKey);
  const [loading, setLoading] = useState(() => Boolean(workspaceId && !freshCacheEntry(cached)));
  const [loadedKey, setLoadedKey] = useState(() => (workspaceId && freshCacheEntry(cached) ? cacheKey : ""));

  useEffect(() => {
    let active = true;

    if (freshCacheEntry(cached)) {
      setSubscription(cached.subscription || null);
      setLoading(false);
      setLoadedKey(cacheKey);
      return () => {
        active = false;
      };
    }

    setSubscription(cached?.subscription || null);

    if (!workspaceId) {
      setLoading(false);
      setLoadedKey(cacheKey);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    const loadPromise = pendingSubscriptionFeatureLoads.get(cacheKey) ||
      identitySubscriptionService.subscriptions
        .activeForWorkspace(selectedWorkspace, actorId)
        .then((nextSubscription) => {
          subscriptionFeatureCache.set(cacheKey, {
            subscription: nextSubscription || null,
            fetchedAt: Date.now(),
          });
          return nextSubscription || null;
        })
        .catch((error) => {
          if (cached) return cached.subscription || null;
          throw error;
        })
        .finally(() => {
          pendingSubscriptionFeatureLoads.delete(cacheKey);
        });

    pendingSubscriptionFeatureLoads.set(cacheKey, loadPromise);
    loadPromise
      .then((nextSubscription) => {
        if (!active) return;
        setSubscription(nextSubscription || null);
        setLoadedKey(cacheKey);
      })
      .catch(() => {
        if (!active) return;
        setSubscription(null);
        setLoadedKey(cacheKey);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [actorId, cacheKey, cached, selectedWorkspace, workspaceId]);

  const features = useMemo(
    () => subscriptionFeaturesFor(subscription, {}),
    [subscription],
  );
  const tier = useMemo(
    () => subscriptionTier(subscription, {}),
    [subscription],
  );

  return {
    features,
    loading: loading || Boolean(workspaceId && loadedKey !== cacheKey),
    subscription,
    tier,
  };
}
